import { z } from 'zod';

import { durationLabel } from '@mds/shared/lib/format';
import { email, phone, picked, required, tooLong } from '@mds/shared/lib/validation';
import type { GuestProposalResource } from '@mds/shared/types/proposal';

/**
 * Mirrors `createGuestFacilityProposalSchema` in the API's facility-proposals
 * module, less `requestId` and `resourceId` (added at submit).
 *
 * Built per room, because the capacity is the room's. `contact` stays nested
 * as the API has it, so a server error on `contact.email` lands under the
 * email field. Opening hours are not checked here: the calendar only offers
 * free hours on open days, and the server has the last word. The minimum
 * length is, because the calendar lets a run shorter than it be picked.
 */
export const proposalSchema = (resource: GuestProposalResource, today: string) =>
  z.object({
    contact: z.object({
      name: required('Full name').max(160, tooLong(160)),
      email,
      phone,
    }),
    organizationName: z.string().trim().max(160, tooLong(160)),
    purpose: required('Purpose').max(5000, tooLong(5000)),
    bookingDate: required('Date').refine((date) => date >= today, 'Pick a date from today on.'),
    // Held as text; the API takes minutes as numbers.
    startMinute: picked('Pick a start time.').transform(Number),
    durationMinutes: picked('Pick how long you need it.')
      .transform(Number)
      .refine(
        (minutes) => minutes >= resource.minimumBookingMinutes,
        `${resource.name} needs at least ${durationLabel(resource.minimumBookingMinutes)}. Click a later time to extend.`,
      ),
    attendeeCount: z.coerce
      .number({ message: 'Enter a number of people.' })
      .int('Enter a whole number of people.')
      .min(1, 'At least one person.')
      .max(resource.capacity, `${resource.name} holds up to ${resource.capacity} people.`),
  });

export type ProposalForm = z.input<ReturnType<typeof proposalSchema>>;
